import { useEffect } from "react";
import { useLocation, useNavigate } from "@tanstack/react-router";
import { SortDirection } from "../types";

const STORAGE_KEY = "judi-view-state";
// sessionStorage flag — restore runs once per tab session. Without this, every
// remount of `<TodoApp />` (which happens when navigating between top-level
// routes since each route instantiates its own copy) would re-fire the restore
// and bounce the user back to the saved view, even when they explicitly
// navigated away.
const RESTORE_FLAG_KEY = "judi-view-state-restored";

const SORT_VALUES: SortDirection[] = ["off", "asc", "desc"];

// Top-level routes whose pathname is restored on next mount. Add any new
// top-level route here so the persistence layer knows it's restorable.
// `/task/$taskId` is intentionally excluded — an open task dialog is not
// part of the saved view.
const RESTORABLE_PATHNAMES = ["/", "/calendar", "/emails", "/captures"] as const;

// Search params that describe board-view filter state. Add any new built-in
// filter / sort URL param here so it's persisted alongside the rest.
const PERSISTED_SEARCH_KEYS = [
  "filter",
  "priorities",
  "lists",
  "tags",
  "scheduled",
  "work_hours",
] as const;

type PersistedSearchKey = (typeof PERSISTED_SEARCH_KEYS)[number];
type PersistedSearch = Partial<Record<PersistedSearchKey, string>>;

interface PersistedViewState {
  pathname: string;
  search: PersistedSearch;
  sortByPriority: SortDirection;
  sortByCreatedDate: SortDirection;
  showCompleted: boolean;
  showArchived: boolean;
  allSubtasksExpanded: boolean;
}

const DEFAULTS: PersistedViewState = {
  pathname: "/",
  search: {},
  sortByPriority: "off",
  sortByCreatedDate: "off",
  showCompleted: false,
  showArchived: false,
  allSubtasksExpanded: false,
};

function isRestorablePathname(p: unknown): p is (typeof RESTORABLE_PATHNAMES)[number] {
  return typeof p === "string" && (RESTORABLE_PATHNAMES as readonly string[]).includes(p);
}

function isSortDirection(v: unknown): v is SortDirection {
  return typeof v === "string" && (SORT_VALUES as readonly string[]).includes(v);
}

function readPersistedState(): PersistedViewState {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULTS;
    const parsed = JSON.parse(raw) as Partial<PersistedViewState>;

    const search: PersistedSearch = {};
    if (parsed.search && typeof parsed.search === "object") {
      for (const key of PERSISTED_SEARCH_KEYS) {
        const value = (parsed.search as Record<string, unknown>)[key];
        if (typeof value === "string") search[key] = value;
      }
    }

    return {
      pathname: isRestorablePathname(parsed.pathname) ? parsed.pathname : DEFAULTS.pathname,
      search,
      sortByPriority: isSortDirection(parsed.sortByPriority) ? parsed.sortByPriority : "off",
      sortByCreatedDate: isSortDirection(parsed.sortByCreatedDate)
        ? parsed.sortByCreatedDate
        : "off",
      showCompleted: typeof parsed.showCompleted === "boolean" ? parsed.showCompleted : false,
      showArchived: typeof parsed.showArchived === "boolean" ? parsed.showArchived : false,
      allSubtasksExpanded:
        typeof parsed.allSubtasksExpanded === "boolean" ? parsed.allSubtasksExpanded : false,
    };
  } catch {
    return DEFAULTS;
  }
}

function writePersistedState(state: PersistedViewState): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // localStorage unavailable (private browsing, quota exceeded). Persistence
    // gracefully degrades — the app still works, just without restore.
  }
}

function sessionStorageGetItem(key: string): string | null {
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

function sessionStorageSetItem(key: string, value: string): void {
  try {
    sessionStorage.setItem(key, value);
  } catch {
    // Same graceful-degradation reasoning as writePersistedState.
  }
}

/**
 * Initial values for the toggle/sort state that ViewContext owns. Each call
 * reads and parses the whole saved snapshot, so call it once per mount and
 * hand the same result to every initializer rather than calling it per field.
 */
export function getInitialPersistedToggles() {
  const s = readPersistedState();
  return {
    sortByPriority: s.sortByPriority,
    sortByCreatedDate: s.sortByCreatedDate,
    showCompleted: s.showCompleted,
    showArchived: s.showArchived,
    allSubtasksExpanded: s.allSubtasksExpanded,
  };
}

interface UseViewStatePersistenceArgs {
  filter: string | undefined;
  priorities: string | undefined;
  lists: string | undefined;
  tags: string | undefined;
  scheduled: string | undefined;
  // Optional so existing test fixtures that predate the work-hours filter
  // keep compiling without mechanical touches. Treated identically to
  // an explicit `undefined` at runtime.
  work_hours?: string | undefined;
  sortByPriority: SortDirection;
  sortByCreatedDate: SortDirection;
  showCompleted: boolean;
  showArchived: boolean;
  allSubtasksExpanded: boolean;
}

/**
 * Persists the user's view state to localStorage and restores it on the
 * initial mount of TodoApp.
 *
 * Persisted: route (`/`, `/calendar`, `/emails`), board-view filter/sort
 * search params, sort directions, visibility toggles. `/task/$taskId` is
 * excluded — opening a task is not part of the saved view, so refreshes
 * on a task page stay there but new tabs don't reopen the task.
 *
 * Restore runs once on mount, only when the URL is the "blank entry"
 * (`/` with no search params). Anything else — a shared filter link, a
 * deep-link to /calendar, a refresh on `/?filter=abc` — wins over the
 * saved snapshot so URLs remain authoritative when explicit.
 */
export function useViewStatePersistence({
  filter,
  priorities,
  lists,
  tags,
  scheduled,
  work_hours,
  sortByPriority,
  sortByCreatedDate,
  showCompleted,
  showArchived,
  allSubtasksExpanded,
}: UseViewStatePersistenceArgs) {
  const { pathname } = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (sessionStorageGetItem(RESTORE_FLAG_KEY)) return;
    sessionStorageSetItem(RESTORE_FLAG_KEY, "1");

    const hasAnySearch =
      filter !== undefined ||
      priorities !== undefined ||
      lists !== undefined ||
      tags !== undefined ||
      scheduled !== undefined ||
      work_hours !== undefined;
    const isBlankEntry = pathname === "/" && !hasAnySearch;
    if (!isBlankEntry) return;

    const saved = readPersistedState();
    const savedHasSearch = Object.keys(saved.search).length > 0;
    const savedIsBlank = saved.pathname === "/" && !savedHasSearch;
    if (savedIsBlank) return;

    navigate({
      to: saved.pathname,
      search: () => saved.search as Record<string, unknown>,
      replace: true,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- the route and search params are read as the entry snapshot, so this must see them exactly as the tab was opened. Restore is a once-per-tab decision (already enforced by the sessionStorage flag above), and later changes belong to the persist effect below — listing these would only add ways to re-evaluate a decision that has already been made. `navigate` is omitted for a different reason: it is the effect's action, not an input, and TanStack Router keeps its identity stable — but even if that changed, the sessionStorage flag guarantees a second run would return before reaching the call.
  }, []);

  useEffect(() => {
    // Don't update saved pathname/search while a task dialog is open — the
    // user excluded `/task/$taskId` from restoration. Toggles still sync so
    // changes made with a task open aren't lost.
    if (!isRestorablePathname(pathname)) {
      const prev = readPersistedState();
      writePersistedState({
        ...prev,
        sortByPriority,
        sortByCreatedDate,
        showCompleted,
        showArchived,
        allSubtasksExpanded,
      });
      return;
    }

    const search: PersistedSearch = {};
    if (filter !== undefined) search.filter = filter;
    if (priorities !== undefined) search.priorities = priorities;
    if (lists !== undefined) search.lists = lists;
    if (tags !== undefined) search.tags = tags;
    if (scheduled !== undefined) search.scheduled = scheduled;
    if (work_hours !== undefined) search.work_hours = work_hours;

    writePersistedState({
      pathname,
      search,
      sortByPriority,
      sortByCreatedDate,
      showCompleted,
      showArchived,
      allSubtasksExpanded,
    });
  }, [
    pathname,
    filter,
    priorities,
    lists,
    tags,
    scheduled,
    work_hours,
    sortByPriority,
    sortByCreatedDate,
    showCompleted,
    showArchived,
    allSubtasksExpanded,
  ]);
}
